import { Badge } from "../../../components/ui/badge";
import { DASH, formatDateTime, formatNumber, formatRate, humanize, levelTone } from "../format";
import type { QaResult } from "../types";
import { Block, Empty, Quote, Stat, Unavailable, type TurnRef } from "./shared";

export function QaPanel({ result, turnRef }: { result: QaResult; turnRef: TurnRef }) {
  const av = result.availability;
  const t = result.triggers;
  const triggerCount = (t.repetition?.length ?? 0) + (t.dead_air?.length ?? 0) + (t.false_claims?.length ?? 0) + (t.supervision?.length ?? 0);

  return (
    <div className="space-y-5">
      {!av.agent_prompt ? <Unavailable>The agent's prompt was not available, so prompt-specific checks were not made.</Unavailable> : null}
      {!av.timing ? <Unavailable>No turn timing, so dead air was not measured.</Unavailable> : null}
      {!av.supervision_alerts ? <Unavailable>Supervision alerts were not available for this call.</Unavailable> : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat
          label="Score"
          value={formatNumber(result.score, 1)}
          hint={`pass mark ${formatNumber(result.pass_threshold)}`}
        />
        <Stat
          label="Result"
          value={result.passed === null ? DASH : <Badge tone={result.passed ? "success" : "danger"}>{result.passed ? "Passed" : "Failed"}</Badge>}
        />
        <Stat label="Penalty" value={formatNumber(result.penalty, 1)} hint={`${triggerCount} trigger${triggerCount === 1 ? "" : "s"}`} />
        <Stat
          label="Scorecard"
          value={humanize(result.scorecard?.source)}
          hint={result.scorecard?.updated_at ? `updated ${formatDateTime(result.scorecard.updated_at)}` : undefined}
        />
      </div>

      <Block title="Criteria">
        {result.criteria.length ? (
          <ul className="divide-y divide-line rounded-xl border border-line">
            {result.criteria.map((c) => (
              <li key={c.id} className="space-y-1 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-ink">{c.label}</span>
                  <span className="text-xs text-ink-3">weight {c.weight}</span>
                  <Badge tone={c.passed === true ? "success" : c.passed === false ? "danger" : "neutral"}>
                    {c.passed === true ? "Pass" : c.passed === false ? "Fail" : "Not applicable"}
                  </Badge>
                </div>
                {c.reason ? <p className="text-xs text-ink-2">{c.reason}</p> : null}
                <Quote text={c.evidence} />
              </li>
            ))}
          </ul>
        ) : <Empty>No criteria were scored.</Empty>}
        <p className="text-xs text-ink-3">Not applicable criteria are left out of the weighted score.</p>
      </Block>

      <Block title="Triggers">
        {triggerCount ? (
          <ul className="space-y-2 text-sm text-ink-2">
            {t.repetition?.map((r, i) => (
              <li key={`r${i}`}>
                Repeated itself ({formatRate(r.similarity)} similar to {turnRef(r.repeats_turn)})
                <Quote text={r.quote} turn={turnRef(r.turn_index)} />
              </li>
            ))}
            {t.dead_air?.map((d, i) => (
              <li key={`d${i}`}>Dead air of {formatNumber(d.duration_s, 1)} s <span className="text-xs text-ink-3">{turnRef(d.turn_index)}</span></li>
            ))}
            {t.false_claims?.map((f, i) => (
              <li key={`f${i}`}>False claim: {f.claim}<Quote text={f.quote} turn={turnRef(f.turn_index)} /></li>
            ))}
            {t.supervision?.map((s, i) => (
              <li key={`s${i}`} className="flex flex-wrap items-center gap-2">
                <Badge tone={s.severity === "critical" ? "danger" : s.severity === "warn" ? "warning" : levelTone(s.severity)}>{humanize(s.severity)}</Badge>
                {humanize(s.type)}: {s.detail}
                <span className="text-xs text-ink-3">{turnRef(s.turn_index)}</span>
              </li>
            ))}
          </ul>
        ) : <Empty>None.</Empty>}
      </Block>

      {result.rationale ? (
        <Block title="Rationale"><p className="text-sm text-ink-2">{result.rationale}</p></Block>
      ) : null}
    </div>
  );
}
